import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { MetaDataSectionEnum } from 'app/main/pages/settings/documentfields/meta-data.helper';
import { GlobalService } from './global.service';
import uuid from 'short-unique-id';
import { Blocks } from 'app/main/pages/general-editor-tabs/blocks/blocks';
import { DomHandlersService } from './dom-handlers.service';
import { HttpClient } from '@angular/common/http';
import { environment } from 'environments/environment';
import { Block } from 'app/interfaces/blocks';
import { DomHelperService } from './dom-helper.service';
import { BehaviorSubject, lastValueFrom, Observable, take } from 'rxjs';
import { EditTemplateFacade } from 'app/main/pages/edit-template/store/edit-template-store.facade';
import { SessionImageService } from './session-image.service';
import { ICustomFieldDto } from 'app/main/pages/settings/documentfields/metadata-item.dto';

interface ReusableAttachmentEmitter {
  roleId: number;
  metaDataId: number;
  fieldSection?: string;
  uuid?: string;
}

@Injectable({
  providedIn: 'root'
})

export class ReusableAttachmentService {
  public uuid = new uuid({ length: 6, dictionary: 'number' });
  private renderer: Renderer2;
  private reusableAttachmentEmitter = new BehaviorSubject<ReusableAttachmentEmitter>(null);

  constructor(
    private globalService: GlobalService,
    rendererFactory: RendererFactory2,
    private Blocks: Blocks,
    private domHandlersService: DomHandlersService,
    private http: HttpClient,
    private domHelperService: DomHelperService,
    private editTemplateFacade: EditTemplateFacade,
    private sessionImageService: SessionImageService
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  updateAllAttachments(){
     // Guard: ensure fields_data and blocks exist
    const blocksObj = this.globalService?.fields_data?.['blocks'] ?? null;
    if (!blocksObj || typeof blocksObj !== 'object') {
      console.warn('[ReusableAttachmentService][updateAllAttachments] no blocks available, skipping updateAllAttachments');
      return;
    }

    const attachmentBlocks = Object.values(blocksObj).filter(
      (obj: Block) => obj?.type === 'attachment'
    );
    if (!Array.isArray(attachmentBlocks) || attachmentBlocks.length === 0) {
      return;
    }
    for (const attachmentData of attachmentBlocks) {
      this.updateAttachment(attachmentData as Block);
    }
  }

  deleteRoleAttachments(roleId: number | string){
    const blocksObj = this.globalService.fields_data['blocks'];
    const attachmentBlocks = Object.values(blocksObj).filter(
      (obj: Block) => obj.type === 'attachment' && Number(obj?.role_id) === Number(roleId)
    );
    for (const attachmentData of attachmentBlocks) {
      const attachment = attachmentData as Block;
      if(attachment?.is_uploaded){
        this.globalService.updateBlock(attachment.uuid, { is_uploaded: 0});                    
      }
      this.domHandlersService.deleteAttachmentByUuid(attachment?.uuid);
    }
  }

  updateAttachment(attachmentData: Block){
    if(attachmentData?.type === "attachment"){
        if(attachmentData?.meta_data_id && attachmentData?.role_id && Number(attachmentData?.meta_data_id) !== 0 && Number(attachmentData?.role_id) !== 0){
          if(attachmentData?.is_uploaded){
            return;
          }
          const selectedRecipient = this.globalService.roles?.find(obj => obj.id === attachmentData?.role_id);
          if(selectedRecipient && selectedRecipient?.recipient_id){
            let attachment;
            if(attachmentData?.field_section === MetaDataSectionEnum.PERSON_FIELD && selectedRecipient?.recipient?.custom_fields){
              attachment = selectedRecipient?.recipient?.custom_fields.find(obj => obj.meta_data_id === attachmentData?.meta_data_id);
            } else if(attachmentData?.field_section === MetaDataSectionEnum.ORGANIZATION_FIELD && selectedRecipient?.organization_id && selectedRecipient?.organization?.custom_fields){
              attachment = selectedRecipient?.organization?.custom_fields.find(obj => obj.meta_data_id === attachmentData?.meta_data_id);
            }
            if(attachment){
              const files = [{ attachmentUrl: '', filename: attachment.file_name, url: attachment.value, uuid: ''}]
              const attachmentBlockUpload = document.querySelector(`.attachment-block-upload[uuid="${attachmentData.uuid}"]`);
              if (attachmentBlockUpload) {
                const attachmentBeforeUpload = attachmentBlockUpload.querySelector('.attachment-before-upload');
                const attachmentAfterUpload = attachmentBlockUpload.querySelector('.attachment-after-upload');
                const attachmentListItems = attachmentAfterUpload?.querySelector('.attachment-list-items');
                this.renderer.addClass(attachmentBeforeUpload, 'hidden');
                this.renderer.removeClass(attachmentAfterUpload, 'hidden');
                let fileView = '';
                let deleteBtn = false;
                if(attachmentData?.request_from === 0 || attachmentData?.request_from === -1){
                  deleteBtn = true;
                }
                if (Array.isArray(files)) {
                    for (let index = 0; index < files.length; index++) {
                        const uuid = this.uuid();
                        files[index].attachmentUrl = this.streamFile(attachment.id);
                        fileView += this.Blocks.attachmentLinkView(files[index], uuid, attachmentData.uuid, files[index].filename, deleteBtn);
                        files[index].uuid = uuid;
                        attachmentData = this.globalService.updateBlock(attachmentData.uuid, { files: files, attachment_name: files[index].filename, is_uploaded: 0});
                    }
                }
                this.renderer.setProperty(attachmentListItems, 'innerHTML', fileView);
                this.renderer.setStyle(document.body, 'cursor', 'default');
                if(deleteBtn){
                  this.domHelperService.initializeEventListener('.delete-attachment-file', 'click', 'deleteAttachment');
                }
              }
            } else {
               this.removeAttachmentFromBox(attachmentData);
            }
          } else {
            this.removeAttachmentFromBox(attachmentData);
          }
        }
      }
    }

    removeAttachmentFromBox(attachmentData){
      if(attachmentData.files.length > 0 && !attachmentData?.is_uploaded){
        this.deleteAttachmentByUuid(attachmentData.uuid);
      } 
    }

    deleteAttachmentByUuid(uuid){
      this.domHandlersService.deleteAttachmentByUuid(uuid);
    }

    deleteAttachment(attachmentId: number) {
      return this.http.delete(`contact-attachments/${attachmentId}/attachment`);
    }

    streamFile(attachmentId: number) {
      return `${environment.apiUrl}contact-attachments/${attachmentId}/stream-file`;
    }

    async getFileThumbnail(attachment: ICustomFieldDto, runTime: boolean = false): Promise<string> {
      const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
      const extension = attachment?.value.split('.').pop()?.toLowerCase() || '';
      const isImage = imageExtensions.includes(extension);
      const escapedFileName = this.escapeHtml(attachment?.file_name);
      if (isImage) {
        return `
          <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 5px">
            <img src="${runTime? await this.generateThumbnail(attachment) :this.streamFile(attachment?.id)}" width="25px" height="25px" />
            <span>${escapedFileName}</span>
          </div>`;
      } else {
        return escapedFileName;
      }
    }

    async generateThumbnail(attachment: ICustomFieldDto): Promise<string> {
      if (!attachment?.value) {
        return '';
      }
      const templateRoute = await lastValueFrom(this.editTemplateFacade.templateRoute$.pipe(take(1)));
      const section = this.globalService.getTheSectionByRoute(templateRoute.route).toLowerCase();
      const sessionImage: string = this.sessionImageService.getTemplateImages(attachment?.value, Number(templateRoute.id), section);
      return sessionImage;
    }

    escapeHtml(text: string): string {
      const map = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;',
      };
      return text.replace(/[&<>"']/g, m => map[m]);
    }

    /**
       * Patches new data to the reusable attachment emitter.
       * @param reusableAttachmentData The data to be emitted.
       */
      patchReusableAttachment(reusableAttachmentData: ReusableAttachmentEmitter){
        if(reusableAttachmentData.metaDataId && reusableAttachmentData.roleId){
          this.reusableAttachmentEmitter.next(reusableAttachmentData);
        }
      }
  
  
      /**
       * Returns an observable of the reusable attachment data.
       * Components can subscribe to this to receive updates.
       * @returns An Observable of `ReusableAttachmentEmitter`.
       */
      getReusableAttachment(): Observable<ReusableAttachmentEmitter>{
          return this.reusableAttachmentEmitter.asObservable();
      }

}
